<template>
  <div>
    <vx-card id="faxContainer" :title="$t('fax')" class="mb-8 vs-con-loading__container">
      <template slot="actions">
        <vs-button
          color="primary"
          :type="activeTab === 'send' ? 'filled' : 'border'"
          icon-pack="feather"
          icon="icon-send"
          size="small"
          class="mr-2"
          @click="changeTab('send')"
        >{{ $t('fax_send') }}</vs-button>
        <vs-button
          color="primary"
          :type="activeTab === 'outbound' ? 'filled' : 'border'"
          icon-pack="feather"
          icon="icon-upload"
          size="small"
          class="mr-2"
          @click="changeTab('outbound')"
        >{{ $t('fax_outbox') }}</vs-button>
        <vs-button
          color="primary"
          :type="activeTab === 'inbound' ? 'filled' : 'border'"
          icon-pack="feather"
          icon="icon-download"
          size="small"
          @click="changeTab('inbound')"
        >{{ $t('fax_inbox') }}</vs-button>
      </template>

      <!-- 발신 -->
      <div class="vx-row" v-if="activeTab === 'send'">
        <div class="vx-col md:w-1/2 sm:w-full w-full">
          <div class="mb-3">
            <vs-input :label="$t('fax_from_number')" :value="displayCallerId" class="w-full" readonly disabled/>
            <span class="form-text text-muted" v-text="$t('fax_from_number_fixed_hint')"></span>
          </div>

          <div class="mb-3">
            <vs-input :label="$t('fax_to_number')" :placeholder="$t('fax_to_number')" v-model="formData.to_number" class="w-full"/>
            <span class="form-text text-muted" v-text="$t('fax_to_number_hint')"></span>
            <validation-error :model-error="formDataErrors.to_number"></validation-error>
          </div>

          <div class="mb-3">
            <vs-input :label="$t('fax_customer_name')" :placeholder="$t('fax_customer_name')" v-model="formData.customer_name" class="w-full"/>
            <validation-error :model-error="formDataErrors.customer_name"></validation-error>
          </div>

          <div class="mb-3">
            <vs-input :label="$t('memo')" :placeholder="$t('memo')" v-model="formData.memo" class="w-full"/>
            <validation-error :model-error="formDataErrors.memo"></validation-error>
          </div>

          <div class="mb-3">
            <file-input :key="fileInputKey" :label="$t('attachments')" v-model="formData.file" :model="formData.file"></file-input>
            <span class="form-text text-muted" v-text="uploadHint"></span>
            <validation-error :model-error="formDataErrors.file"></validation-error>
          </div>

          <vs-divider></vs-divider>

          <vs-button color="primary" type="filled" :disabled="sending" @click="sendFax">{{ $t('send_fax') }}</vs-button>
        </div>

        <div class="vx-col md:w-1/2 sm:w-full w-full">
          <vs-alert class="h-auto" color="primary" active="true" icon-pack="feather" icon="icon-info">
            <ul class="ml-4" style="list-style: disc;">
              <li v-text="$t('fax_guide_line_1')"></li>
              <li v-text="$t('fax_guide_line_2')"></li>
              <li v-text="$t('fax_guide_line_3')"></li>
            </ul>
          </vs-alert>
        </div>
      </div>

      <!-- 발신함 -->
      <div v-if="activeTab === 'outbound'">
        <!-- 입력 아무 곳에서나 Enter 로 검색되도록 행 단위로 받는다. (keyup 은 버블링된다) -->
        <div class="vx-row mb-4" @keyup.enter="loadOutbound(1)">
          <div class="vx-col md:w-1/4 sm:w-1/2 w-full mb-2">
            <label class="vs-input--label" v-text="$t('date_filter_from')"/>
            <date-picker v-model="outboundFilter.from" :model="outboundFilter.from" :placeholder="$t('date_filter_from')"></date-picker>
          </div>
          <div class="vx-col md:w-1/4 sm:w-1/2 w-full mb-2">
            <label class="vs-input--label" v-text="$t('date_filter_to')"/>
            <date-picker v-model="outboundFilter.to" :model="outboundFilter.to" :placeholder="$t('date_filter_to')"></date-picker>
          </div>
          <div class="vx-col md:w-1/4 sm:w-1/2 w-full mb-2">
            <label class="vs-input--label" v-text="$t('status')"/>
            <select v-model="outboundFilter.status" class="vs-inputx vs-input--input normal hasValue w-full">
              <option value="" v-text="$t('all')"></option>
              <option value="queued" v-text="$t('fax_status_queued')"></option>
              <option value="sending" v-text="$t('fax_status_sending')"></option>
              <option value="success" v-text="$t('fax_status_success')"></option>
              <option value="failed" v-text="$t('fax_status_failed')"></option>
              <option value="cancelled" v-text="$t('fax_status_cancelled')"></option>
            </select>
          </div>
          <div class="vx-col md:w-1/4 sm:w-1/2 w-full mb-2">
            <label class="vs-input--label" v-text="$t('fax_to_number')"/>
            <div class="flex">
              <vs-input :placeholder="$t('fax_to_number')" v-model="outboundFilter.to_number" class="w-full mr-2"/>
              <vs-button color="primary" type="filled" icon-pack="feather" icon="icon-search" @click="loadOutbound(1)"></vs-button>
            </div>
          </div>
        </div>

        <shared-fax-outbound-list
          :faxes="outboundFaxes"
          @openFile="uuid => openFile(uuid, 'outbound')"
        ></shared-fax-outbound-list>

        <div class="mt-4">
          <paginate
            :total="outboundPages"
            :click-handler="loadOutbound"
            v-model="initialPage"
            goto
          ></paginate>
        </div>
      </div>

      <!-- 수신함 -->
      <div v-if="activeTab === 'inbound'">
        <vs-alert
          v-if="receiveState === 'no_permission'"
          class="h-auto"
          color="warning"
          active="true"
          icon-pack="feather"
          icon="icon-lock"
        >
          <h5 class="mb-2" v-text="$t('fax_receive_locked_title')"></h5>
          <p v-text="$t('fax_receive_no_permission')"></p>
        </vs-alert>

        <vs-alert
          v-else-if="receiveState === 'awaiting_did'"
          class="h-auto"
          color="warning"
          active="true"
          icon-pack="feather"
          icon="icon-clock"
        >
          <h5 class="mb-2" v-text="$t('fax_receive_awaiting_did_title')"></h5>
          <p v-text="$t('fax_receive_awaiting_did')"></p>
        </vs-alert>

        <div v-else>
          <div class="vx-row mb-4" @keyup.enter="loadInbound(1)">
            <div class="vx-col md:w-1/4 sm:w-1/2 w-full mb-2">
              <label class="vs-input--label" v-text="$t('date_filter_from')"/>
              <date-picker v-model="inboundFilter.from" :model="inboundFilter.from" :placeholder="$t('date_filter_from')"></date-picker>
            </div>
            <div class="vx-col md:w-1/4 sm:w-1/2 w-full mb-2">
              <label class="vs-input--label" v-text="$t('date_filter_to')"/>
              <date-picker v-model="inboundFilter.to" :model="inboundFilter.to" :placeholder="$t('date_filter_to')"></date-picker>
            </div>
            <div class="vx-col md:w-1/4 sm:w-1/2 w-full mb-2">
              <label class="vs-input--label" v-text="$t('fax_caller_id')"/>
              <div class="flex">
                <vs-input :placeholder="$t('fax_caller_id')" v-model="inboundFilter.caller_id" class="w-full mr-2"/>
                <vs-button color="primary" type="filled" icon-pack="feather" icon="icon-search" @click="loadInbound(1)"></vs-button>
              </div>
            </div>
            <div class="vx-col md:w-1/4 sm:w-1/2 w-full mb-2">
              <label class="vs-input--label" v-text="$t('fax_my_did')"/>
              <p class="mt-2" v-text="faxDids.join(', ')"></p>
            </div>
          </div>

          <shared-fax-inbound-list
            :faxes="inboundFaxes"
            @openFile="uuid => openFile(uuid, 'inbound')"
            @markRead="markRead"
          ></shared-fax-inbound-list>

          <div class="mt-4">
            <paginate
              :total="inboundPages"
              :click-handler="loadInbound"
              v-model="initialPage"
              goto
            ></paginate>
          </div>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import Fax from '@/models/Fax'
import Paginate from '@/mixins/paginate'
import DatePicker from '@/components/form-inputs/DatePicker'
import SharedFaxOutboundList from '@/views/Shared/Fax/OutboundList'
import SharedFaxInboundList from '@/views/Shared/Fax/InboundList'
import Moment from 'moment'

// 발신 직후에는 큐 -> 발신중 -> 결과 순으로 상태가 바뀐다.
// 진행 중인 건이 남아 있는 동안만 발신함을 주기적으로 다시 읽는다.
const REFRESH_INTERVAL_MS = 8000

export default {
  name: 'User.Fax',
  mixins: [Paginate],
  components: {
    DatePicker,
    SharedFaxOutboundList,
    SharedFaxInboundList
  },
  data () {
    const today = Moment().format('YYYY-MM-DD')

    return {
      activeTab: 'send',
      sending: false,
      callerId: '',
      maxUploadMb: 20,
      allowedTypes: [],
      receiveState: 'no_permission',
      faxDids: [],
      formData: {
        to_number: '',
        customer_name: '',
        memo: '',
        file: null
      },
      formDataErrors: {},
      // FileInput 은 내부 상태를 들고 있어 발신 후 key 를 바꿔 초기화한다.
      fileInputKey: 0,
      outboundFaxes: [],
      outboundPages: 0,
      outboundFilter: {
        from: today,
        to: today,
        status: '',
        to_number: ''
      },
      inboundFaxes: [],
      inboundPages: 0,
      inboundFilter: {
        from: today,
        to: today,
        caller_id: ''
      },
      refreshTimer: null
    }
  },
  computed: {
    displayCallerId () {
      return this.formatNumber(this.callerId)
    },
    uploadHint () {
      const types = this.allowedTypes.join(', ').toUpperCase()
      return this.$t('fax_upload_hint', { types, size: this.maxUploadMb })
    },
    hasPendingOutbound () {
      return this.outboundFaxes.some(fax => fax.status === 'queued' || fax.status === 'sending')
    }
  },
  async mounted () {
    await this.loadStatus()
  },
  beforeDestroy () {
    this.stopAutoRefresh()
  },
  methods: {
    async loadStatus () {
      try {
        const response = await Fax.status()
        const data = response.data.data

        this.callerId = data.callerId
        this.receiveState = data.receiveState
        this.faxDids = data.faxDids || []
        this.maxUploadMb = data.maxUploadMb
        this.allowedTypes = data.allowedTypes || []
      } catch (e) {
        this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
      }
    },

    changeTab (tab) {
      this.activeTab = tab
      this.stopAutoRefresh()

      if (tab === 'outbound') {
        this.loadOutbound(1)
      } else if (tab === 'inbound' && this.receiveState === 'ready') {
        this.loadInbound(1)
      }
    },

    async sendFax () {
      this.formDataErrors = {}

      if (!this.formData.to_number) {
        this.formDataErrors = { to_number: [this.$t('fax_to_number_required')] }
        return
      }

      if (!this.formData.file) {
        this.formDataErrors = { file: [this.$t('fax_file_required')] }
        return
      }

      const confirm = await this.$swalHelper.warning({
        title: this.$t('fax_send_confirm_title'),
        text: this.$t('fax_send_confirm', { to: this.formData.to_number }),
        confirmButton: this.$t('send'),
        cancelButton: this.$t('cancel')
      })

      if (!confirm.value) {
        return
      }

      this.sending = true
      this.$vs.loading({ container: '#faxContainer' })

      let queued = false

      try {
        await Fax.send(this.formData)
        queued = true
      } catch (e) {
        const result = this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
        this.formDataErrors = result.validationErrors
      } finally {
        this.sending = false
        this.$vs.loading.close('#faxContainer > .con-vs-loading')
      }

      if (!queued) {
        return
      }

      this.$swalHelper.success({
        title: this.$t('fax_send_success'),
        confirmButton: this.$t('confirm')
      })

      this.resetForm()
      // 로딩 오버레이를 먼저 닫고 나서 발신함을 연다. (loadOutbound 가 다시 연다)
      this.changeTab('outbound')
    },

    resetForm () {
      this.formData = {
        to_number: '',
        customer_name: '',
        memo: '',
        file: null
      }
      this.formDataErrors = {}
      this.fileInputKey += 1
    },

    async loadOutbound (page = 1) {
      this.$vs.loading({ container: '#faxContainer' })

      try {
        const response = await Fax.outbound({ ...this.outboundFilter, page })

        this.outboundFaxes = response.data.data.faxes
        this.outboundPages = response.data.meta.pagination.last_page
        this.initialPage = page

        this.scheduleAutoRefresh(page)
      } catch (e) {
        this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
      } finally {
        this.$vs.loading.close('#faxContainer > .con-vs-loading')
      }
    },

    async loadInbound (page = 1) {
      this.$vs.loading({ container: '#faxContainer' })

      try {
        const response = await Fax.inbound({ ...this.inboundFilter, page })

        this.inboundFaxes = response.data.data.faxes
        this.inboundPages = response.data.meta.pagination.last_page
        this.initialPage = page
      } catch (e) {
        // 수신 권한이 중간에 회수된 경우 상태를 다시 반영한다.
        if (e.response && e.response.status === 403 && e.response.data.data) {
          this.receiveState = e.response.data.data.receiveState
          return
        }

        this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
      } finally {
        this.$vs.loading.close('#faxContainer > .con-vs-loading')
      }
    },

    scheduleAutoRefresh (page) {
      this.stopAutoRefresh()

      if (!this.hasPendingOutbound || this.activeTab !== 'outbound') {
        return
      }

      this.refreshTimer = setTimeout(() => {
        this.loadOutbound(page)
      }, REFRESH_INTERVAL_MS)
    },

    stopAutoRefresh () {
      if (this.refreshTimer !== null) {
        clearTimeout(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    async openFile (uuid, direction) {
      this.$vs.loading({ container: '#faxContainer' })

      try {
        const response = await Fax.file({ uuid, direction })
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))

        window.open(url, '_blank')

        // 새 탭이 파일을 읽을 시간을 준 뒤 해제한다.
        setTimeout(() => window.URL.revokeObjectURL(url), 60000)
      } catch (e) {
        this.$swalHelper.error({
          title: 'Error!',
          text: this.$t('fax_file_open_failed'),
          confirmButton: this.$t('confirm')
        })
      } finally {
        this.$vs.loading.close('#faxContainer > .con-vs-loading')
      }
    },

    async markRead (uuid) {
      try {
        await Fax.markRead({ uuid })
        this.loadInbound(this.initialPage)
      } catch (e) {
        this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
      }
    },

    formatNumber (number) {
      if (!number) {
        return ''
      }

      // 070-4689-2120 형태로 보기 좋게 끊는다.
      if (number.length === 11) {
        return `${number.substr(0, 3)}-${number.substr(3, 4)}-${number.substr(7, 4)}`
      }

      return number
    }
  }
}
</script>

<style scoped>

</style>
